import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { configService } from '@/app/services/configService'
import { ProductsConfigResponseNew } from '@/app/services/configService/getProductsConfig'
import { useAuth } from '@/app/hooks/useAuth'
import { PartnerType } from '@/app/utils/partners.enum'
import { useToast } from '@/view/components/ui/use-toast'
import { TriStateEnum } from '@/app/entities/TriState'
import masks from '@/app/utils/masks'

const schema = z.object({
  name: z.string(),
  tableCode: z.string(),
  percents: z.string(),
  maxPercents: z.string(),
  bonus: z.string().optional(),
  leaderCommission: z.string().optional(),
  limit: z.string().optional(),
})

export type FormCreateTableData = z.infer<typeof schema>

type PartnerRenderConfig = {
  title: string
  renderLabel: (item: any, index: number, items: any[]) => string
  renderValue: (item: any) => string
  itemsToShow: (items: any[]) => any[]
}

export const usePartnersPageController = () => {
  const { PERMISSIONS } = useAuth()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: productsConfig, isLoading } =
    useQuery<ProductsConfigResponseNew>({
      queryKey: ['productsConfig'],
      queryFn: configService.getProductsConfig,
    })

  const { mutateAsync: updateProductState } = useMutation({
    mutationFn: async ({
      partner,
      state,
    }: {
      partner: PartnerType
      state: TriStateEnum
    }) => {
      const currentProduct = productsConfig?.products[partner];
      const isActive = currentProduct?.isActive ?? false;

      return configService.patchConfigProducts({
        type: partner,
        state,
        isActive,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productsConfig'] })
      toast({ title: 'Produto atualizado com sucesso' })
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar produto',
        variant: 'destructive',
      })
    },
  })

  const handleProductStateChange = async (
    partner: PartnerType,
    state: TriStateEnum,
  ) => {
    await updateProductState({ partner, state })
  }

  const [selectedPartner, setSelectedPartner] = useState<PartnerType | null>(
    null,
  )

  const [selectedPartnerData, setSelectedPartnerData] = useState<{
    partner: PartnerType
    itens: any[]
    tableId?: string
    delay?: number
    isActive?: boolean
    state?: TriStateEnum
  } | null>(null)

  const methods = useForm<FormCreateTableData>({
    resolver: zodResolver(schema),
  })

  const partnersConfig: Record<PartnerType, PartnerRenderConfig> = {
    FGTS: {
      title: 'Quantidade de tabelas',
      renderLabel: (item) => {
        return item.label
      },
      renderValue: (item) =>
        item.label === 'limit'
          ? masks.currencyAllPlatforms(item.value)
          : `${item.value}%`,
      itemsToShow: (items) => items.slice(0, 3),
    },
    CREFAZ: {
      title: 'Parcelas e comissões',
      renderLabel: (item) => {
        return `A partir de ${item.label} parcelas`
      },
      renderValue: (item) => `${item.value}%`,
      itemsToShow: (items) => items,
    },
    SALVCAR: {
      title: 'Valores e comissões',
      renderLabel: (item) => masks.currencyAllPlatforms(item.label),
      renderValue: (item) => masks.currencyAllPlatforms(item.value),
      itemsToShow: (items) => items,
    },
    PAN: {
      title: 'Comissão',
      renderLabel: (item) => {
        switch (item.label) {
          case 'percents':
            return 'Comissão vendedor'
          case 'leaderCommission':
            return 'Comissão líder'
          case 'max':
            return 'Máximo'
          default:
            return item.label
        }
      },
      renderValue: (item) => `${item.value}%`,
      itemsToShow: (items) => items.filter((item) => item.label !== 'limit' && item.label !== 'bonus'),
    },
    CREFISA: {
      title: 'Valores e comissões',
      renderLabel: (item) => masks.currencyAllPlatforms(item.label),
      renderValue: (item) => `${item.value}%`,
      itemsToShow: (items) => items,
    },
  }

  return {
    productsConfig,
    isLoading,
    PERMISSIONS,
    selectedPartner,
    setSelectedPartner,
    selectedPartnerData,
    setSelectedPartnerData,
    methods,
    handleProductStateChange,
    partnersConfig,
  }
}
